import { useState, ReactNode } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useNavigate } from 'react-router-dom';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid2';
import AutoStoriesRoundedIcon from '@mui/icons-material/AutoStoriesRounded';
import GraphicEqRoundedIcon from '@mui/icons-material/GraphicEqRounded';
import PlayCircleOutlineRoundedIcon from '@mui/icons-material/PlayCircleOutlineRounded';
import { styled } from '@mui/material/styles';
import { CulturalPost } from '../../../types';
import { formatDate } from '../../../utils';

const StyledCard = styled(Card)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: 0,
  height: '100%',
  backgroundColor: theme.palette.background.paper,
  borderRadius: '14px',
  borderColor: theme.palette.divider,
  boxShadow: '0 8px 22px rgba(40, 38, 28, 0.06)',
  '&:hover': {
    cursor: 'pointer',
    transform: 'translateY(-2px)',
    boxShadow: '0 14px 30px rgba(40, 38, 28, 0.12)',
  },
  '&:focus-visible': {
    outline: '3px solid',
    outlineColor: 'hsla(210, 98%, 48%, 0.5)',
    outlineOffset: '2px',
  },
  transition: 'transform 0.25s ease, box-shadow 0.25s ease',
}));

const StyledCardContent = styled(CardContent)({
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  padding: 16,
  flexGrow: 1,
  '&:last-child': {
    paddingBottom: 16,
  },
});

type HubListProps = {
  posts: CulturalPost[];
  isNextPage?: boolean;
};

type MediaMeta = {
  label: string;
  icon: ReactNode;
  gradient: string;
};

export default function HubList({ posts, isNextPage }: HubListProps) {
  const navigate = useNavigate();
  const [focusedCardIndex, setFocusedCardIndex] = useState<number | null>(null);

  const handleFocus = (index: number) => {
    setFocusedCardIndex(index);
  };

  const handleBlur = () => {
    setFocusedCardIndex(null);
  };

  const getMediaMeta = (post: CulturalPost): MediaMeta => {
    if (post.video_url) {
      return {
        label: 'Video record',
        icon: <PlayCircleOutlineRoundedIcon sx={{ fontSize: 30 }} />,
        gradient:
          'linear-gradient(135deg, rgba(69, 88, 119, 0.95), rgba(41, 56, 81, 0.95))',
      };
    }

    if (post.audio_url) {
      return {
        label: 'Audio record',
        icon: <GraphicEqRoundedIcon sx={{ fontSize: 30 }} />,
        gradient:
          'linear-gradient(135deg, rgba(190, 132, 76, 0.95), rgba(148, 95, 56, 0.95))',
      };
    }

    return {
      label: 'Story record',
      icon: <AutoStoriesRoundedIcon sx={{ fontSize: 30 }} />,
      gradient:
        'linear-gradient(135deg, rgba(95, 117, 87, 0.95), rgba(68, 84, 61, 0.95))',
    };
  };

  return (
    <>
      <Divider />
      {!isNextPage && (
        <Typography variant="h3" color="primary" gutterBottom fontWeight="bold">
          More Records
        </Typography>
      )}
      <Grid container spacing={2} columns={12}>
        {posts?.map((post, index) => {
          const mediaMeta = getMediaMeta(post);

          return (
            <Grid key={`hublist_${index}`} size={{ xs: 12, md: 4 }}>
              <StyledCard
                variant="outlined"
                onFocus={() => handleFocus(index)}
                onBlur={handleBlur}
                tabIndex={0}
                className={focusedCardIndex === index ? 'Mui-focused' : ''}
                onClick={() => navigate(`/hub/${post.id}`)}
              >
                {post?.image_url ? (
                  <CardMedia
                    component="img"
                    alt="hublist_image"
                    image={post.image_url}
                    sx={{
                      width: '100%',
                      height: 230,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      objectFit: 'cover',
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: '100%',
                      height: 230,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: 1,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      background: mediaMeta.gradient,
                      color: '#fff',
                    }}
                  >
                    {mediaMeta.icon}
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {mediaMeta.label}
                    </Typography>
                  </Box>
                )}
                <StyledCardContent>
                  <Typography gutterBottom variant="h6" component="div">
                    {post.title}
                  </Typography>
                </StyledCardContent>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 2,
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '16px',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: 1,
                      alignItems: 'center',
                    }}
                  >
                    <Typography variant="caption">LegacyTies team</Typography>
                  </Box>
                  <Typography variant="caption">
                    {formatDate(post.updated_at)}
                  </Typography>
                </Box>
              </StyledCard>
            </Grid>
          );
        })}
      </Grid>
    </>
  );
}
